import { Notification } from '../types';
import { getPostgresApiBaseUrl } from './postgresClient';
import type { CompanyNotificationPayload } from '../utils/employeeNotificationMessages';

interface NotificationRow {
  id: string;
  title: string;
  message: string;
  type: 'add' | 'delete' | 'info';
  created_at: string;
}

/**
 * Monta URL absoluta ou relativa para rotas de notificação.
 * @param postgresApiUrl - URL base da API
 * @param path - Caminho da rota
 * @returns URL completa
 */
function buildNotificationUrl(postgresApiUrl: string, path: string): string {
  const base = getPostgresApiBaseUrl(postgresApiUrl);
  return base ? `${base}${path}` : path;
}

/**
 * Converte linha do banco para tipo Notification do frontend.
 * @param row - Registro de rh_notifications
 * @returns Notificação para o sino
 */
export function mapNotificationRow(row: NotificationRow): Notification {
  return {
    id: row.id,
    title: row.title,
    message: row.message,
    type: row.type,
    timestamp: row.created_at,
    read: false,
    source: 'server',
  };
}

/**
 * Busca notificações do usuário logado na empresa.
 * @param postgresApiUrl - URL da API
 * @param companyId - UUID da empresa
 * @returns Lista de notificações ou erro
 */
export async function fetchCompanyNotifications(
  postgresApiUrl: string,
  companyId: string,
): Promise<{ data: Notification[]; error: Error | null }> {
  try {
    const params = new URLSearchParams({ company_id: companyId });
    const url = buildNotificationUrl(postgresApiUrl, `/queries/notifications?${params.toString()}`);
    const res = await fetch(url, { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = typeof body?.error === 'string' ? body.error : `Erro HTTP ${res.status}`;
      return { data: [], error: new Error(msg) };
    }
    const rows = Array.isArray(body?.data) ? (body.data as NotificationRow[]) : [];
    return { data: rows.map(mapNotificationRow), error: null };
  } catch (error: unknown) {
    return {
      data: [],
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * Propaga notificações para todos os não-admins da empresa.
 * @param postgresApiUrl - URL da API
 * @param companyId - UUID da empresa
 * @param payloads - Mensagens a enviar
 * @returns Erro, se houver
 */
export async function broadcastCompanyNotifications(
  postgresApiUrl: string,
  companyId: string,
  payloads: CompanyNotificationPayload[],
): Promise<Error | null> {
  if (!payloads.length) return null;

  try {
    for (const payload of payloads) {
      const url = buildNotificationUrl(postgresApiUrl, '/queries/notifications/broadcast');
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          title: payload.title,
          message: payload.message,
          type: payload.type,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = typeof body?.error === 'string' ? body.error : `Erro HTTP ${res.status}`;
        return new Error(msg);
      }
    }
    return null;
  } catch (error: unknown) {
    return error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Remove uma notificação específica do usuário logado.
 * @param postgresApiUrl - URL da API
 * @param companyId - UUID da empresa
 * @param notificationId - UUID da notificação
 * @returns Erro, se houver
 */
export async function dismissCompanyNotification(
  postgresApiUrl: string,
  companyId: string,
  notificationId: string,
): Promise<Error | null> {
  try {
    const params = new URLSearchParams({ company_id: companyId });
    const url = buildNotificationUrl(
      postgresApiUrl,
      `/queries/notifications/${notificationId}?${params.toString()}`,
    );
    const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg = typeof body?.error === 'string' ? body.error : `Erro HTTP ${res.status}`;
      return new Error(msg);
    }
    return null;
  } catch (error: unknown) {
    return error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Remove todas as notificações do usuário logado na empresa.
 * @param postgresApiUrl - URL da API
 * @param companyId - UUID da empresa
 * @returns Erro, se houver
 */
export async function clearCompanyNotifications(
  postgresApiUrl: string,
  companyId: string,
): Promise<Error | null> {
  try {
    const params = new URLSearchParams({ company_id: companyId });
    const url = buildNotificationUrl(postgresApiUrl, `/queries/notifications?${params.toString()}`);
    const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg = typeof body?.error === 'string' ? body.error : `Erro HTTP ${res.status}`;
      return new Error(msg);
    }
    return null;
  } catch (error: unknown) {
    return error instanceof Error ? error : new Error(String(error));
  }
}
